// CustomMultiSelect.js
import React, { useEffect, useRef, useState } from "react";
import { Input, Modal } from "antd";
import { CloseOutlined, DownOutlined } from "@ant-design/icons";
import CheckboxGroup from "./CheckboxGroup"; // Import the CheckboxGroup component
import "./CustomMutliSelectStyles.scss";
import CustomSelect from "./CustomSelect";

const CustomMultiSelect = ({
  options,
  onChange,
  isFieldFocused,
  qnId,
  multi_resp,
  form,
  placeholderText,
  setIsFieldFocused,
  answer,
  setValue,
  grouped_choices,
  defaultValue,
  name,
  selection_placeholder,
  setselectField,
  choice_has_val,
  other_placeholder,
  choice_value_placeholder,
  IsClearEnabledDisabled,
  isSearchEnabled,
  ...rest
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [changedCheckboxIds, setChangedCheckboxIds] = useState([]);
  const [modalPosition, setModalPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const inputRef = useRef(null);
  const [textFieldValues, setTextFieldValues] = useState({});

  const placeCheckboxModal = () => {
    const inputRect = inputRef.current.getBoundingClientRect();
    setModalPosition({
      top: inputRect.bottom + (qnId || changedCheckboxIds.length ? 8 : 22),
      left: inputRect.left,
      width: inputRect.width,
    });
  };

  const showModal = () => {
    placeCheckboxModal();
    setIsModalVisible(true);
  };

  const handleCancel = async () => {
    if (!form) {
      setIsModalVisible(false);
    } else {
      let isNumberFieldErrorExists = false;

      try {
        const allUnchecked = changedCheckboxIds.length === 0;

        // If all checkboxes are unchecked, close the modal
        if (allUnchecked) {
          setIsModalVisible(false);
        } else {
          // Otherwise, validate fields related to the changed checkboxes
          const textAndTotalFields = changedCheckboxIds.reduce((fields, id) => {
            const numberFieldErrors = form.getFieldError(id + "total");
            fields.push(id + "text", id + "total");

            if (numberFieldErrors.length > 0) {
              isNumberFieldErrorExists = true;
            }
            return fields;
          }, []);

          // Get the field list in the modal
          const values = await form.validateFields(textAndTotalFields);

          // Check if any text field has a required error
          const hasRequiredError = Object.keys(values).some((field) => {
            const errors = form.getFieldError(field);
            return errors && errors.length > 0;
          });

          // If there are no number field errors and no required errors, close the modal
          if (!isNumberFieldErrorExists && !hasRequiredError) {
            setIsModalVisible(false);
          }
        }
      } catch (errorInfo) {
        // Delay to ensure the error elements have been rendered
        setTimeout(() => {
          const firstErrorField = errorInfo?.errorFields[0]?.name[0];

          const firstErrorElement = document.querySelector(
            `[id="${firstErrorField}"]`
          );

          // Scroll to the first error if it exists
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 0); // 0ms delay to wait for the DOM update
      }
    }
  };

  const handleTextFieldChange = (optionProps) => {
    const { checkboxId, value, option } = optionProps;
    setTextFieldValues((prevValues) => ({
      ...prevValues,
      [checkboxId]: { option, value },
    }));
  };

  const handleCheckboxChange = (checkedValues) => {
    changedCheckboxIds.forEach((prevCheckedField) => {
      if (!checkedValues.includes(prevCheckedField)) {
        setTextFieldValues((prevValues) => {
          const updatedValues = { ...prevValues };
          delete updatedValues[prevCheckedField];
          return updatedValues;
        });

        form && form.setFieldsValue({ [prevCheckedField]: undefined });
      }
    });
    setChangedCheckboxIds(checkedValues);
  };

  useEffect(() => {
    if (isModalVisible && !isFieldFocused) {
      setIsFieldFocused(true);
    }
  }, [isFieldFocused]);

  useEffect(() => {
    if (changedCheckboxIds?.length > 0) {
      setValue(selection_placeholder);
    } else {
      if (qnId) {
        form.setFieldsValue({
          [qnId]: null,
        });
      }
      setValue("");
    }

    if (onChange) {
      if (qnId) {
        const multiChoiceValues = changedCheckboxIds
          .filter(
            (checkboxId) => textFieldValues[checkboxId]?.value !== undefined
          )
          .map((checkboxId) => ({
            opted_answer_id: checkboxId,
            choice_value: textFieldValues[checkboxId]?.value || 0,
            opted_answer_text: textFieldValues[checkboxId]?.option,
          }));
        const payload = {
          question_id: qnId,
          multi_choice_values: multiChoiceValues,
        };

        form.setFieldsValue({
          [qnId]: changedCheckboxIds.length > 0 ? changedCheckboxIds : null,
        });
        multiChoiceValues.length > 0 && onChange(payload);
      } else {
        onChange({ [name]: changedCheckboxIds });
      }
    }
  }, [changedCheckboxIds, textFieldValues]);

  useEffect(() => {
    // filling in answers - preassessment fields
    let ansArray = [];

    if (answer && answer.length > 0) {
      ansArray = answer.map((ans) => {
        return {
          answerId: ans?.opted_answer_id,
          value: ans?.choice_value,
          opted_answer_text: ans?.opted_answer_text,
        };
      });

      const answerIds = ansArray.map((ans) => ans.answerId);
      const values = ansArray.map((ans) => ans.value);

      setChangedCheckboxIds(answerIds);
      setTextFieldValues((prevValues) => ({
        ...prevValues,
        ...Object.fromEntries(
          ansArray.map((ans) => [
            ans.answerId,
            { option: ans.opted_answer_text, value: ans.value },
          ])
        ),
      }));
      if (qnId)
        form.setFieldsValue({
          [qnId]: answerIds || values,
        });
    }
  }, [answer]);
  useEffect(() => {
    // filling in answers
    if (defaultValue) {
      if (multi_resp) {
        if (defaultValue.length > 0)
          if (isSearchEnabled) {
            setChangedCheckboxIds(defaultValue);
          } else {
            const answerId = defaultValue?.map((ans) => ans?.id);
            setChangedCheckboxIds(answerId);
            form &&
              form.setFieldsValue({
                [name]: answerId,
              });
          }
      } else {
        setChangedCheckboxIds([defaultValue?.id]);
        form &&
          form.setFieldsValue({
            [name]: defaultValue?.id,
          });
      }
    }
  }, [defaultValue]);
  const commonCheckboxGroupProps = {
    handleCheckboxChange,
    checkedFields: changedCheckboxIds,
    handleTextFieldChange,
    choice_has_val,
    textFieldValues,
    form,
    other_placeholder,
    choice_value_placeholder,
  };
  return (
    <>
      <div
        ref={inputRef}
        className="custom-multiselect-container"
        onClick={showModal}
      >
        <Input
          className="login-input"
          placeholder={!isFieldFocused && placeholderText}
          bordered={false}
          value={
            isFieldFocused || changedCheckboxIds?.length > 0
              ? selection_placeholder
              : ""
          }
          suffix={
            <>
              {isFieldFocused || changedCheckboxIds?.length > 0 ? (
                <span className="selected-ids">
                  {changedCheckboxIds?.length}
                </span>
              ) : null}
              <DownOutlined className="down-arrow" />
            </>
          }
          onFocus={(e) => {
            setselectField(true);
          }}
        />
      </div>
      {isSearchEnabled && isModalVisible ? (
        <CustomSelect
          options={options}
          handleSelection={setChangedCheckboxIds}
          handleCancel={handleCancel}
          defaultValue={changedCheckboxIds}
          {...rest}
        />
      ) : (
        <Modal
          className="custom-dropdown-modal"
          style={{
            top: `${modalPosition.top}px`,
            left: `${modalPosition.left}px`,
          }}
          visible={isModalVisible}
          footer={null}
          closable={false}
          afterClose={() =>
            changedCheckboxIds.length === 0 && setIsFieldFocused(false)
          }
          maskClosable={true}
          onCancel={handleCancel}
          mask={false}
          width={modalPosition?.width}
          focusTriggerAfterClose={false}
        >
          <div className="modal-content">
            <div className="checkbox-group-container">
              {grouped_choices ? (
                options.map((op) => (
                  <CheckboxGroup
                    key={op?.group}
                    checkboxes={op?.options}
                    groupName={op?.group}
                    {...commonCheckboxGroupProps}
                  />
                ))
              ) : (
                <CheckboxGroup
                  checkboxes={options}
                  {...commonCheckboxGroupProps}
                />
              )}
            </div>
            {!IsClearEnabledDisabled && (
              <div
                className="clear-all-option"
                onClick={() => {
                  setChangedCheckboxIds([]);
                  handleCheckboxChange([]);
                }}
              >
                <CloseOutlined />
                CLEAR ALL
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default CustomMultiSelect;
